import React, { useRef, useState } from "react";
import { AiFillDelete } from "react-icons/ai";
import expand from "../../utils/expand.png";
import { format } from "date-fns";
import { Globe, Mail, Phone, Share2, Sparkles, User } from "lucide-react";

const TableGrouped = ({
  handleInputChange,
  groupedLeads,
  statusMapping,
  setSelectedLeads,
  selectedLeads,
  handleViewLead,
  calculateAgedDays,
  openStatusModal,
  onDeleteChecked,
  editingRowId,
}) => {
  const newRowRef = useRef();
  const [expandedGroups, setExpandedGroups] = useState({});

  const statusOrder = [
    "newLead",
    "contacted",
    "attemptedToContact",
    "qualified",
    "unQualified",
  ];

  const sortedGroupedLeads = Object.keys(groupedLeads)
    .sort((a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b))
    .reduce((sorted, key) => {
      sorted[key] = groupedLeads[key];
      return sorted;
    }, {});

  const getStatusStyle = (status) => {
    switch (status) {
      case "newLead":
        return "bg-orange-100 text-orange-700";
      case "contacted":
        return "bg-blue-100 text-blue-700";
      case "attemptedToContact":
        return "bg-red-100 text-red-700";
      case "qualified":
        return "bg-green-100 text-green-700";
      case "unQualified":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };
  const toggleGroup = (status) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [status]: prev[status] === undefined ? false : !prev[status],
    }));
  };
  return (
    <div>
      {/* Delete Button */}
      {selectedLeads.length > 0 && (
        <button
          onClick={onDeleteChecked}
          className="mb-3 flex items-center gap-2 bg-red-500 text-white px-3 py-2 rounded-lg"
        >
          <AiFillDelete />
          Delete Selected
        </button>
      )}

      {Object.entries(sortedGroupedLeads).map(([status, leads]) => {
        const isExpanded =
          expandedGroups[status] !== undefined ? expandedGroups[status] : true;
        return (
          <div
            key={status}
            className="mb-6 bg-white border rounded-xl shadow-sm overflow-hidden"
          >
            {/* Section Header */}
            <div
              onClick={() => toggleGroup(status)}
              className="px-4 py-2 bg-slate-50 border-b text-sm font-semibold text-slate-600 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {statusMapping[status]}
                <span className="px-2 py-0.5 bg-slate-200 rounded-full text-[10px] text-slate-500">
                  {leads.length}
                </span>
              </div>

              {/* Accordion Icon */}
              <div
                className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`}
              >
                <svg
                  className="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {isExpanded && (
              <div className="overflow-x-auto max-h-[400px]">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>

                      <th className="px-3 py-2 text-left w-[150px]">Lead</th>

                      <th className="px-3 py-2 text-left w-[220px]">
                        Customer
                      </th>

                      <th className="px-3 py-2 text-left w-[250px]">Contact</th>

                      <th className="px-3 py-2 text-left w-[160px]">Sales</th>

                      <th className="px-3 py-2 text-left w-[140px]">Project</th>

                      <th className="px-3 py-2 text-left w-[120px]">Status</th>

                      <th className="px-3 py-2 text-left w-[80px]">Location</th>

                      <th className="px-3 py-2 text-left w-[100px]">Source</th>
                      <th className="px-3 py-2 text-left w-[80px]">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {leads.map((row) => (
                      <tr
                        key={row.lead_id}
                        ref={row.lead_id === editingRowId ? newRowRef : null}
                        className="hover:bg-slate-50"
                      >
                        {/* Checkbox */}
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(row.lead_id)}
                            onChange={() =>
                              setSelectedLeads((prev) =>
                                prev.includes(row.lead_id)
                                  ? prev.filter((id) => id !== row.lead_id)
                                  : [...prev, row.lead_id],
                              )
                            }
                          />
                        </td>

                        {/* Lead Column */}
                        <td className="px-3 py-2">
                          <div className="flex flex-col text-xs">
                            <span className="font-semibold text-slate-700">
                              #{row.lead_id}
                            </span>

                            <span className="text-slate-500">
                              {format(row.lead_entryTime, "dd MMM yyyy")}
                            </span>

                            {/* AGE moved here */}
                            <span className="text-blue-600 font-medium text-[11px]">
                              {calculateAgedDays(row.lead_entryTime)}
                            </span>
                          </div>
                        </td>

                        {/* Customer */}
                        <td className="px-3 py-2">
                          <div
                            className=" max-w-[200px] line-clamp-2"
                            title={row.lead_organization}
                          >
                            {row.lead_organization}
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-3 py-2">
                          <div className="flex flex-col text-xs">
                            <span className="font-medium">
                              {row.lead_primarycontact}
                            </span>

                            <span className="text-slate-500">
                              {row.lead_phone}
                            </span>

                            <span className="text-blue-600 truncate">
                              {row.lead_email}
                            </span>
                          </div>
                        </td>

                        {/* Sales */}
                        <td
                          className="px-3 py-2 text-xs truncate max-w-[140px]"
                          title={row.lead_salespersonName}
                        >
                          {row.lead_salespersonName}
                        </td>

                        {/* Project */}
                        <td className="px-3 py-2">{row.lead_CampaignId}</td>

                        {/* Status */}
                        <td
                          className="px-3 py-2 cursor-pointer"
                          onClick={() => openStatusModal(row)}
                        >
                          <div
                            className={`
    flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
    transition-all duration-200 hover:shadow-sm active:scale-95
    ${getStatusStyle(row.lead_status)}
  `}
                          >
                            <span className="truncate">
                              {statusMapping[row.lead_status] ||
                                "Select Status"}
                            </span>

                            {/* Dropdown Arrow Icon */}
                            <svg
                              className="w-3 h-3 opacity-60"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </td>

                        {/* Location */}
                        <td className="px-3 py-2">
                          {row.lead_assignedto ? (
                            <a
                              href={`https://www.google.com/maps?q=${row.lead_assignedto}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 text-xs"
                            >
                              View
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1.5">
                            {row.lead_source
                              ?.split(",")
                              .filter((src) => src.trim() !== "")
                              .map((source, index) => {
                                const name = source.trim().toLowerCase();

                                // 1. Define a clean, icon-based configuration
                                const sourceConfig = {
                                  ai: {
                                    label: "AI ",
                                    icon: <Sparkles size={12} />,
                                    theme:
                                      "bg-purple-50 text-purple-700 border-purple-200",
                                  },
                                  email: {
                                    label: "Email",
                                    icon: <Mail size={12} />,
                                    theme:
                                      "bg-blue-50 text-blue-700 border-blue-200",
                                  },
                                  phone: {
                                    label: "Phone ",
                                    icon: <Phone size={12} />,
                                    theme:
                                      "bg-green-50 text-green-700 border-green-200",
                                  },
                                  walkin: {
                                    label: "Walk-in",
                                    icon: <User size={12} />,
                                    theme:
                                      "bg-slate-50 text-slate-700 border-slate-200",
                                  },
                                  whatsapp: {
                                    label: "WhatsApp",
                                    icon: <Share2 size={12} />,
                                    theme:
                                      "bg-emerald-50 text-emerald-700 border-emerald-200",
                                  },
                                };

                                // 2. Match logic
                                const matchKey = Object.keys(sourceConfig).find(
                                  (key) => name.includes(key),
                                );
                                const config = sourceConfig[matchKey] || {
                                  label: source.trim(),
                                  icon: <Globe size={12} />,
                                  theme:
                                    "bg-gray-50 text-gray-600 border-gray-200",
                                };

                                return (
                                  <span
                                    key={index}
                                    className={`
              inline-flex items-center gap-1.5 px-2 py-0.5 
              text-[10px] font-semibold uppercase tracking-tight
              rounded border shadow-sm
              ${config.theme}
            `}
                                  >
                                    {config.icon}
                                    {config.label}
                                  </span>
                                );
                              })}

                            {!row.lead_source && (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleViewLead(row)}
                            className="
      p-1
      text-blue-600
      hover:bg-white
      rounded
      border border-slate-200
      transition-all
      shadow-sm
    "
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TableGrouped;
